/**
 * GTM Consent Mode Bootstrap
 *
 * Loads Google Tag Manager with Advanced Consent Mode, ported from the
 * website's `GoogleTagManager.tsx`. No-op without a container id
 * (`VITE_PUBLIC_GTM_CONTAINER_ID` unset, empty or `off`): no dataLayer is
 * created and gtm.js is never requested.
 *
 * Push order, all before gtm.js is requested:
 *   1-4. The four `consent default` blocks of `REGION_CONSENT_DEFAULTS`, in
 *        array order; `region` only when non-empty, `wait_for_update` only
 *        on the global block (it rides in that block's signals).
 *   5.   `set ads_data_redaction true`.
 *   6.   Under GPC: `consent update` denying the three `ad_*` signals, then
 *        `{event: "gpc_detected", gpc_active: true}`.
 *   7.   For a stored decision: `consent update` replaying it (no
 *        `consent_updated`: the replay precedes `gtm.start`).
 *   8.   `{"gtm.start": <ms>, event: "gtm.js"}`, then the async script.
 *
 * Afterwards every decision change (save, acknowledge, a newer server row)
 * pushes `consent update` then `{event: "consent_updated"}`. Undecided
 * states (resolved-defaults seeds, settle, reconciling) are never echoed.
 * Every gtag command is an `arguments` object via `gtagCommand()`; there is
 * no `<noscript>` fallback because it cannot honour Consent Mode.
 */

import { ensureDataLayer, getGtmContainerId } from "../analytics/dataLayer";
import { REGION_CONSENT_DEFAULTS } from "./consentRegions";
import {
  buildConsentSignals,
  CONSENT_UPDATED_EVENT,
  GPC_AD_DENIAL_SIGNALS,
  GPC_DETECTED_EVENT,
  gtagCommand,
} from "./gtmConsentMode";
import type { ConsentGlobal, ConsentState } from "./core";

/** The gtm.js URL; the container id is appended as `?id=`. */
export const GTM_SCRIPT_URL = "https://www.googletagmanager.com/gtm.js";

/** The core functions the bootstrap reads. */
export type GtmConsentSource = Pick<ConsentGlobal, "getState" | "subscribe">;

// Module guards, all reset by `__resetGtmForTests()`.
let bootstrapped = false;
// True once the subscription's synchronous first call has been seen.
let initialStateObserved = false;
// The last decision key seen, so non-decision changes are skipped.
let lastKey: string | null = null;
let unsubscribe: (() => void) | null = null;

/**
 * The fields that make a decision distinct.
 *
 * @param state - a consent state snapshot
 * @returns `hasDecided|analytics|advertising|gpcActive`
 */
function decisionKey(state: ConsentState): string {
  return `${state.hasDecided}|${state.categories.analytics}|${state.categories.advertising}|${state.gpcActive}`;
}

/**
 * Bootstrap GTM once per page: push the Consent Mode sequence, inject the
 * async gtm.js script, then mirror every later decision. Call after
 * `initConsent()` so the stored decision and GPC are already in `getState()`.
 *
 * @param source - the core's `getState` and `subscribe`
 */
export function bootstrapGtm({ getState, subscribe }: GtmConsentSource): void {
  if (bootstrapped) return;
  const id = getGtmContainerId();
  if (!id) return;
  bootstrapped = true;
  const dl = ensureDataLayer()!;

  // Array order is the contract: never sorted or deduped. `region?.length`
  // keeps an empty list from scoping a block to nobody.
  for (const block of REGION_CONSENT_DEFAULTS) {
    dl.push(
      gtagCommand(
        "consent",
        "default",
        block.region?.length
          ? { ...block.signals, region: block.region }
          : { ...block.signals },
      ),
    );
  }

  dl.push(gtagCommand("set", "ads_data_redaction", true));

  // Categories are already GPC-locked by the core; `gpcActive` is memoised.
  const state = getState();
  if (state.gpcActive) {
    dl.push(gtagCommand("consent", "update", GPC_AD_DENIAL_SIGNALS));
    dl.push({ event: GPC_DETECTED_EVENT, gpc_active: true });
  }
  if (state.hasDecided) {
    dl.push(
      gtagCommand(
        "consent",
        "update",
        buildConsentSignals(state.categories, state.gpcActive),
      ),
    );
  }

  dl.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTM_SCRIPT_URL}?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  unsubscribe = subscribe(onState);
}

/**
 * Push `consent update` + `consent_updated` when the decision changed. The
 * first (synchronous) call is what the bootstrap already pushed; undecided
 * states are seeds or settle steps, which GTM already holds as defaults.
 *
 * @param state - the new consent state
 */
function onState(state: ConsentState): void {
  const key = decisionKey(state);
  if (!initialStateObserved) {
    initialStateObserved = true;
    lastKey = key;
    return;
  }
  if (key === lastKey) return;
  lastKey = key;
  if (!state.hasDecided) return;

  const dl = ensureDataLayer();
  if (!dl) return;
  dl.push(
    gtagCommand(
      "consent",
      "update",
      buildConsentSignals(state.categories, state.gpcActive),
    ),
  );
  // After the update, so GTM evaluates the event against the new state.
  dl.push({ event: CONSENT_UPDATED_EVENT });
}

/**
 * Test-only reset: clears the guards and unsubscribes. Leaves the DOM and
 * `window.dataLayer` alone.
 */
export function __resetGtmForTests(): void {
  bootstrapped = false;
  initialStateObserved = false;
  lastKey = null;
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
